import { Instance, SnapshotIn, types } from "mobx-state-tree"
import { IAttribute } from "../../models/data/attribute"
import { ICollectionModel } from "../../models/data/collection"
import { ICaseCreation } from "../../models/data/data-set-types"
import { isFiniteNumber } from "../../utilities/math-utils"
import { getTileCaseMetadata, getTileDataSet } from "../../models/shared/shared-data-tile-utils"
import { ISharedModel } from "../../models/shared/shared-model"
import { ITileContentModel, TileContentModel } from "../../models/tiles/tile-content"
import { getNumFormatterForAttribute } from "../case-tile-common/attribute-format-utils"
import { kCaseCardMinWidth, kCaseCardTileType } from "./case-card-defs"

export const CaseCardModel = TileContentModel
  .named("CaseCardModel")
  .props({
    type: types.optional(types.literal(kCaseCardTileType), kCaseCardTileType),
    // key is collection id; value is percentage width
    attributeColumnWidths: types.map(types.number),
  })
  .volatile(() => ({
    animationLevel: Infinity,
    // assertion is load-bearing: widens the literal to the union so the field stays assignable to "left"
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    animationDirection: "right" as "left" | "right",
    animationTimeout: undefined as ReturnType<typeof setTimeout> | undefined
  }))
  .views(self => ({
    get data() {
      return getTileDataSet(self)
    },
    get metadata() {
      return getTileCaseMetadata(self)
    },
    get minWidth() {
      return kCaseCardMinWidth
    },
    // value is a percentage width
    attributeColumnWidth(collectionId: string) {
      return self.attributeColumnWidths.get(collectionId)
    }
  }))
  .views(self => ({
    get summarizedCollections() {
      const selectedItems = self.data?.selection
      const items = self.data?.items
      const collections = self.data?.collections ?? []

      const collectionIdsToSummarize = new Set<string>()

      if (items && selectedItems?.size !== 1) {
        collections.forEach((collection, index) => {
          if (index < collections.length - 1) {
            const cases = self.data?.getCasesForCollection(collection.id) ?? []
            const collectionSelectedCaseIds = self.data?.partiallySelectedCaseIdsByCollection[index]
            if (cases.length > 1 && collectionSelectedCaseIds?.size !== 1) {
              collectionIdsToSummarize.add(collection.id)
            }
          } else {
            // always summarize the last collection
            collectionIdsToSummarize.add(collection.id)
          }
        })
      }

      return collectionIdsToSummarize
    }
  }))
  .views(self => ({
    groupChildCases(parentCaseId: string) {
      const parentCaseInfo = self.data?.caseInfoMap.get(parentCaseId)
      if (!parentCaseInfo?.childCaseIds) return undefined
      return parentCaseInfo.childCaseIds
        .map(childCaseId => self.data?.caseInfoMap.get(childCaseId)?.groupedCase)
        .filter(groupedCase => !!groupedCase)
    },
    // Summarizes the values of `attr` over `collection`. With no selection the summary
    // covers the whole collection (the global "Summarize Dataset" state); when there is a
    // selection it narrows to the collection's cases that have a selected descendant item.
    // Because selecting/navigating to a parent selects all of its descendants, single-parent
    // navigation yields exactly that parent's children (with multiple parents selected it
    // covers every case with a selected descendant). This mirrors the selection-aware header
    // count and matches legacy v2 behavior. Empty values are excluded, as in v2.
    summarizedValues(attr: IAttribute, collection: ICollectionModel) {
      // Establish a MobX dependency on the attribute's mutation counter so this view
      // re-evaluates when individual values change. Volatile strValues/numValues are
      // not deep-observable; changeCount is bumped by setValue/setComputedValues.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      attr.changeCount
      const summaryCases = (self.data?.selection.size ?? 0) > 0
        ? collection.cases.filter(c => self.data?.isAnyChildItemSelected(c.__id__))
        : collection.cases
      // Returns a string summarizing the values of the attribute over the given cases
      if (attr.isNumeric) {
        // Track min/max incrementally rather than building an array and spreading it into
        // Math.min/Math.max, which can overflow the call stack on very large collections.
        let minValue = Infinity
        let maxValue = -Infinity
        let hasNumericValue = false
        summaryCases.forEach(c => {
          const value = self.data?.getNumeric(c.__id__, attr.id)
          if (isFiniteNumber(value)) {
            if (value < minValue) minValue = value
            if (value > maxValue) maxValue = value
            hasNumericValue = true
          }
        })
        if (!hasNumericValue) return ""
        const formatter = getNumFormatterForAttribute(attr)
        const minValueStr = formatter?.(minValue) ?? minValue.toString()
        const maxValueStr = formatter?.(maxValue) ?? maxValue.toString()
        const valueString = minValueStr === maxValueStr ? minValueStr : `${minValueStr}-${maxValueStr}`
        const attrUnits = attr.units ? ` ${attr.units}` : ""
        return `${valueString}${attrUnits}`
      } else {
        const allValues = summaryCases
          .map(c => self.data?.getValue(c.__id__, attr.id))
          .filter(value => value != null && value !== "")
        const uniqueValues = new Set(allValues)
        if (uniqueValues.size > 2) {
          return `${uniqueValues.size} values`
        } else {
          const uniqueValuesArray = Array.from(uniqueValues)
          if (uniqueValuesArray.length === 0) return ""
          if (uniqueValuesArray.length === 2) {
            return `${uniqueValuesArray[0]}, ${uniqueValuesArray[1]}`
          }
          return `${uniqueValuesArray[0]}`
        }
      }
    }
  }))
  .actions(self => ({
    setAnimationLevel(level: number) {
      self.animationLevel = level
    },
    setAnimationDirection(direction: "left" | "right") {
      self.animationDirection = direction
    },
    setAnimationTimeout(timeout: ReturnType<typeof setTimeout>) {
      clearTimeout(self.animationTimeout)
      self.animationTimeout = timeout
    },
    updateAfterSharedModelChanges(sharedModel?: ISharedModel) {
      // TODO
    },
    setAttributeColumnWidth(collectionId: string, width?: number) {
      if (width) {
        self.attributeColumnWidths.set(collectionId, width)
      }
      else {
        self.attributeColumnWidths.delete(collectionId)
      }
    },
    addNewCase(level: number) {
      const selectedParentCaseIds = self.data?.partiallySelectedCaseIdsByCollection[level - 1]
      const selectedParentCaseId = selectedParentCaseIds?.size === 1
        ? Array.from(selectedParentCaseIds)[0] : undefined
      const newCase: ICaseCreation = selectedParentCaseId != null ?
        self.data?.getParentValues(selectedParentCaseId) ?? {} : {}

      const [newCaseId] = self.data?.addCases([newCase]) ?? []

      // TODO: Figure out why this call to validateCases is necessary. The Cypress test for adding a case
      // fails if it isn't here, though adding a case seems to work fine when tested manually.
      self.data?.validateCases()

      return newCaseId
    }
  }))
export interface ICaseCardModel extends Instance<typeof CaseCardModel> { }
export interface ICaseCardSnapshot extends SnapshotIn<typeof CaseCardModel> { }

export function isCaseCardModel(model?: ITileContentModel): model is ICaseCardModel {
  return model?.type === kCaseCardTileType
}
